import React, { useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { Button, Modal } from "../../../components/ui";
import {
  ACCEPTED_ATTR,
  fileSizeLabel,
  uploadProblem,
} from "../services/dishImport";

interface BulkDishUploadModalProps {
  open: boolean;
  onClose: () => void;
  /** Fetches the blank workbook. Rejects with what the server said. */
  onDownloadTemplate: () => Promise<void>;
  /** Sends the filled one. Rejects with what the server said. */
  onImport: (file: File) => Promise<void>;
}

/**
 * The bulk upload: a template to fill in, and the filled sheet coming back.
 *
 * Both halves are in one dialog because they are one job done in two sittings
 * — the sheet is downloaded, filled in somewhere else, and brought back — and
 * a reader who opens this a second time needs the drop zone, not the
 * instructions. Hence the order: the template card is the smaller of the two
 * and sits above, the drop zone takes the room.
 *
 * Nothing is parsed here. The workbook goes to the server as it stands, which
 * is what keeps one set of rules about what a valid sheet is; this dialog only
 * turns away what plainly cannot be one — the wrong extension, or more than
 * 5MB of it — so that a reader who picked the wrong file hears so at the
 * dialog rather than after a round trip.
 */
const BulkDishUploadModal: React.FC<BulkDishUploadModalProps> = ({
  open,
  onClose,
  onDownloadTemplate,
  onImport,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);

  // A dialog closed on a failed import and opened again should ask afresh
  // rather than reopening onto the file that would not go and the message
  // about it. Cleared on the way in rather than on the way out, so nothing
  // flickers underneath the panel as it leaves.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setError("");
    setDragging(false);
  }, [open]);

  const busy = importing || downloading;

  const take = (picked: File | undefined) => {
    if (!picked) return;

    const problem = uploadProblem(picked);
    if (problem) {
      // The bad file is not kept: Import must not be live over a file that has
      // already been turned away.
      setFile(null);
      setError(problem);
      return;
    }

    setError("");
    setFile(picked);
  };

  const handleDownload = async () => {
    if (busy) return;
    setDownloading(true);
    try {
      await onDownloadTemplate();
    } finally {
      setDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!file || busy) return;
    setImporting(true);
    try {
      await onImport(file);
      // Closing is the page's to do, once it has said so and re-read the list.
    } catch (cause) {
      // Said here rather than in a toast: the dialog is still up, the sheet is
      // still the thing being talked about, and a message about a column in the
      // wrong place belongs beside the file it is about.
      setError(
        cause instanceof Error
          ? cause.message
          : "We could not upload that file. Try again in a moment."
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Bulk dish upload"
      // Not while the sheet is with the server: the upload is going out either
      // way, and a dialog closed over it would leave nothing to say so.
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="md"
            onClick={() => void handleImport()}
            loading={importing}
            // Nothing to import until a file has been taken. The primary
            // variant's disabled fill is the pale teal the design asks for, so
            // the button still reads as the one to press once there is.
            disabled={!file || busy}
          >
            Import
          </Button>
        </>
      }
    >
      {/* The template, and the one rule about it. Tinted rather than plain
          because it is the half a first-time reader needs, and the drop zone
          below is where the eye goes otherwise. */}
      <div className="flex items-start justify-between gap-4 rounded-[6px] border border-[#A6EDF5] bg-[#F4FDFE] px-4 py-3.5">
        <div className="min-w-0">
          <p className="text-[14px] font-medium text-[#00424B]">
            Download excel template
          </p>
          <p className="mt-1 text-[11px] 3xl:text-[12px] leading-4 text-ink-muted">
            Use the template as it is. Don&apos;t rename, move, add, delete
            columns, or change formatting.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={busy}
          className="flex flex-shrink-0 items-center gap-1.5 rounded text-[13px] font-medium text-[#00899B] hover:text-[#006A76] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="w-4 h-4" aria-hidden="true" />
          )}
          {downloading ? "Downloading…" : "Download"}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_ATTR}
        aria-label="Choose an Excel file of dishes"
        disabled={busy}
        // The same file picked twice in a row is still a change worth acting
        // on — after a failed import it is the natural way to retry.
        onClick={(event) => {
          (event.target as HTMLInputElement).value = "";
        }}
        onChange={(event) => take(event.target.files?.[0])}
        className="sr-only"
      />

      {/* The drop zone. A div rather than a label wrapping the input: the
          browse link is the one thing in it that should open the picker, and a
          label would make the whole panel — file row and remove button
          included — open it too. */}
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!busy) take(event.dataTransfer.files?.[0]);
        }}
        className={`mt-4 rounded-[6px] border border-dashed px-6 py-8 text-center transition-colors ${
          dragging
            ? "border-[#00C2CB] bg-[#F4FDFE]"
            : "border-[#D7DFE3] bg-[#FCFDFD]"
        }`}
      >
        {file ? (
          // Once a file is in hand the panel stops asking for one and shows
          // what it has: the prompt underneath would be a second invitation to
          // do the thing that has already been done.
          <div className="mx-auto flex max-w-[380px] items-center gap-3 rounded-[6px] border border-line bg-white px-3.5 py-2.5 text-left">
            <FileSpreadsheet
              className="w-5 h-5 flex-shrink-0 text-[#00899B]"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-ink">
                {file.name}
              </span>
              <span className="block text-[12px] text-ink-subtle">
                {fileSizeLabel(file.size)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setError("");
              }}
              disabled={busy}
              aria-label={`Remove ${file.name}`}
              className="flex-shrink-0 rounded p-1 text-ink-subtle hover:bg-[#F4F7F8] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="mx-auto w-6 h-6 text-[#00424B]" aria-hidden="true" />
            <p className="mt-3 text-[14px] text-ink">
              Drag &amp; drop your Excel file here
            </p>
            <p className="mt-1 text-[14px] text-ink-muted">
              or{" "}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="rounded font-medium text-[#00899B] underline underline-offset-2 hover:text-[#006A76] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                browse files
              </button>
            </p>
            <p className="mt-3 text-[11px] 3xl:text-[12px] text-ink-subtle">
              Supports .xls, .xlsx formats (Max 5MB)
            </p>
          </>
        )}
      </div>

      {/* Under the zone rather than inside it, so the panel does not change
          height as messages come and go. `role="alert"` because the reader may
          well still be looking at where the file picker was. */}
      {error && (
        <p role="alert" className="mt-2 text-[12px] text-[#D92D20]">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default BulkDishUploadModal;
